// src/pages/MisVentas.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebaseConfig";

import "./MisVentas.css";

const PAGE_SIZE = 100;

function startOfMonthDate(year, monthIndex0) {
  // monthIndex0: 0 = Enero
  return new Date(year, monthIndex0, 1, 0, 0, 0, 0);
}

function startOfNextMonthDate(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 1, 0, 0, 0, 0);
}

function formatDateAny(value) {
  // soporta Timestamp de Firestore (toDate), Date, o string
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      const d = value.toDate();
      return d.toLocaleString();
    }
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === "string") return value;
    return "—";
  } catch {
    return "—";
  }
}

export default function MisVentas() {
  const navigate = useNavigate();

  const [uid, setUid] = useState(undefined); // undefined=cargando, null=no logueado, string=uid

  // selector mes/año (default: actual)
  const now = useMemo(() => new Date(), []);
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth()); // 0-11

  const [items, setItems] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // búsqueda en cliente
  const [search, setSearch] = useState("");

  const months = useMemo(
    () => [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
    []
  );

  const years = useMemo(() => {
    // 10-12 años hacia atrás (ajústalo a tu gusto)
    const cur = new Date().getFullYear();
    const arr = [];
    for (let y = cur; y >= cur - 12; y--) arr.push(y);
    return arr;
  }, []);

  // 1) Sesión
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);

      // reset lista al cambiar usuario
      setItems([]);
      setLastDoc(null);
      setHasMore(true);
      setErrorMsg("");
    });
    return () => unsub();
  }, []);

  // 2) Cargar primera página al tener uid + al cambiar mes/año
  useEffect(() => {
    if (!uid) return; // null o undefined
    loadPage({ isFirst: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, selYear, selMonth]);

  const buildQuery = ({ isFirst }) => {
    const ventasRef = collection(db, "usuarios", uid, "ventas");

    // rango del mes seleccionado
    const start = startOfMonthDate(selYear, selMonth);
    const end = startOfNextMonthDate(selYear, selMonth);

    // ✅ recomendado: createdAt (Timestamp)
    let q = query(
      ventasRef,
      where("createdAt", ">=", start),
      where("createdAt", "<", end),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );

    if (!isFirst && lastDoc) {
      q = query(
        ventasRef,
        where("createdAt", ">=", start),
        where("createdAt", "<", end),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );
    }

    return q;
  };

  const loadPage = async ({ isFirst }) => {
    if (!uid) return;
    if (loading) return;
    if (!isFirst && !hasMore) return;

    setLoading(true);
    setErrorMsg("");

    try {
      const q = buildQuery({ isFirst });
      const snap = await getDocs(q);
      const docs = snap.docs;

      const newItems = docs.map((d) => ({ id: d.id, ...d.data() }));

      setItems((prev) => {
        if (isFirst) return newItems;

        // evitar duplicados
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const it of newItems) if (!seen.has(it.id)) merged.push(it);
        return merged;
      });

      const last = docs.length > 0 ? docs[docs.length - 1] : null;
      setLastDoc(last);
      setHasMore(docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.message || "Error al cargar ventas");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((v) => {
      const titulo = String(v.titulo || "").toLowerCase();
      const productoId = String(v.productoId || v.ptoductoId || "").toLowerCase();
      const userName = String(v.userName || "").toLowerCase();
      return (
        titulo.includes(q) ||
        productoId.includes(q) ||
        userName.includes(q)
      );
    });
  }, [items, search]);

  // UI estados
  if (uid === undefined) {
    return <div className="ventasWrap">Cargando sesión…</div>;
  }

  if (uid === null) {
    return (
      <div className="ventasWrap">
        <h2>Mis ventas</h2>
        <p>Necesitas iniciar sesión para ver tus ventas.</p>
        <button className="btnPrimary" onClick={() => navigate("/login")}>
          Iniciar sesión
        </button>
      </div>
    );
  }

  return (
    <div className="ventasWrap">
      <div className="ventasHeader">
        <h2>Mis ventas</h2>

        <div className="filtersRow">
          <div className="field">
            <label>Mes</label>
            <select
              value={selMonth}
              onChange={(e) => setSelMonth(Number(e.target.value))}
            >
              {months.map((m, idx) => (
                <option key={m} value={idx}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Año</label>
            <select
              value={selYear}
              onChange={(e) => setSelYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="field grow">
            <label>Búsqueda</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, productoId o userName…"
            />
          </div>
        </div>
      </div>

      {errorMsg ? <div className="errorBox">{errorMsg}</div> : null}

      <div className="ventasList">
        {filteredItems.length === 0 && !loading ? (
          <div className="emptyBox">No hay ventas en este mes.</div>
        ) : (
          filteredItems.map((v) => {
            const productoId = v.productoId || v.ptoductoId || "—";

            return (
              <div
                key={v.id}
                className="ventaItem"
                onClick={() => navigate(`/ventas/${v.id}`)} // ✅ detalle después
                role="button"
                tabIndex={0}
              >
                <div className="ventaTop">
                  <div className="ventaTitulo">{v.titulo || "Sin título"}</div>
                  <div className="ventaFecha">
                    {formatDateAny(v.fechaAgregado || v.createdAt)}
                  </div>
                </div>

                <div className="ventaMid">
                  <div className="ventaMeta">
                    Variante: <b>{v.tituloVariante || "—"}</b>
                  </div>
                  <div className="ventaMeta">
                    ProductoId: <b>{productoId}</b>
                  </div>
                </div>

                <div className="ventaBottom">
                  <div className="ventaMeta">
                    Precio unitario: <b>{v.precioUnitario ?? "—"}</b>
                  </div>
                  <div className="ventaMeta">
                    Cantidad: <b>{v.cantidad ?? "—"}</b>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="ventasFooter">
        <button
          className="btnSecondary"
          onClick={() => loadPage({ isFirst: false })}
          disabled={loading || !hasMore}
        >
          {loading ? "Cargando…" : hasMore ? "Cargar más" : "No hay más"}
        </button>
      </div>
    </div>
  );
}